import _Input, { PropsType as _InputPropsType } from 'components/Legacy/Input'
import * as React from 'react'
import { injectIntl, IntlShape, WrappedComponentProps } from 'react-intl'
import styled, { css } from 'styled-components'
import { color } from 'utils'

import _PasswordInput from '../Legacy/Password'
import { FieldErrorType, InputSizeType } from '../types'
import renderLoader from './renderLoader'
import { active, checkbox, checked, input, label, loader, span, svg } from './theme'

export const defaultProps = {
  showError: true,
  showPassword: false,
  size: 'md' as InputSizeType,
  type: 'text',
  variant: 'normal'
}

export type PropsType = Partial<_InputPropsType> & {
  asyncValidate?: boolean
  e2e?: string
  fieldErrorActive?: void | boolean
  focus?: boolean
  input?: any
  loading?: boolean
  meta?: any
  readOnly?: boolean
  ref: any
  renderSuffix?: (disabled?: boolean) => React.ReactNode
  showError?: boolean
  showPassword?: boolean
  size?: InputSizeType
  touched?: boolean
  type?: string
  variant: string
}

const inputCss = css<PropsType>`
  input {
    ${input};
    border: 2px solid
      ${({ fieldErrorActive, touched }) =>
        fieldErrorActive && touched ? color('dark900') : 'default'};

    &:active:not(:disabled),
    &:focus:not(:disabled) {
      ${active};
    }

    &:active:not(:disabled):not([readonly]),
    &:focus:not(:disabled):not([readonly]) {
      ${active};
    }
  }

  label {
    ${label};
  }

  input[type='checkbox'] + label {
    ${checkbox};
  }

  input[type='checkbox']:checked + label {
    ${checked};
  }

  span {
    ${span};
  }
`

const StyledInput = styled(_Input).attrs<PropsType>((p) => (p.readOnly ? { readonly: true } : {}))`
  ${inputCss}

  input + svg {
    pointer-events: none;
    ${svg};
  }

  input + .loader {
    pointer-events: none;
    ${loader};
  }
`

const StyledPasswordInput = styled(_PasswordInput)`
  ${inputCss}

  input + svg {
    ${svg};
  }

  input + .loader {
    pointer-events: none;
    ${loader};
  }
`

const renderError = (error: FieldErrorType, intl: IntlShape) => {
  if (typeof error === 'string') {
    return error
  }

  if (typeof error === 'object') {
    return intl.formatMessage(error.id, error.values)
  }

  return undefined
}

class Input extends React.Component<PropsType & WrappedComponentProps> {
  static defaultProps = defaultProps

  input = React.createRef<HTMLInputElement>()

  componentDidMount() {
    if (this.props.focus !== true) {
      return
    }

    if (this.props.ref && this.props.ref.current) {
      this.props.ref.current.focus()
    }

    if (this.input && this.input.current) {
      this.input.current.focus()
    }
  }

  render() {
    const { asyncValidate, intl, loading, ref, showError, showPassword, type, ...rest } = this.props
    const Comp = type === 'password' && showPassword === true ? StyledPasswordInput : StyledInput
    const dirty = rest.meta?.dirty ?? false
    const error = rest.meta?.error
    const touched = rest.meta?.touched
    const errorData =
      showError === true && [touched, asyncValidate === true && dirty].includes(true)
        ? error
        : undefined
    const renderSuffix = loading === true ? renderLoader : rest.renderSuffix
    const name = rest?.input?.name ?? ''

    return (
      // @ts-ignore no idea what's up with "Comp' does not have any construct or call signatures."
      <Comp
        {...rest}
        asyncValidate={asyncValidate}
        error={renderError(errorData, intl)}
        name={name}
        ref={ref ?? this.input}
        renderSuffix={renderSuffix}
        touched={touched}
        type={type}
      />
    )
  }
}

// FIXME Couldn't figure out this one
// @ts-ignore
const WithIntl = injectIntl(Input)

export default React.forwardRef<HTMLInputElement, PropsType>((props, ref) => (
  <WithIntl {...props} ref={ref} />
))
